<template>
  <BaseDialog
    :title="$t('common.startAssessment')"
    :is-open="isOpen"
    type="responsive"
    :align-dismiss-button-right="true"
    @close="emit('close-dialog')"
  >
    <LoadingWrapper :state="templateLoadingStatus">
      <ul class="flex flex-col">
        <template v-if="step === 'select-members-and-template'">
          <SurveyMembersForm
            v-model:feedback-sender-ids="form.coacheeIds"
            :label="$t('coaching.addCoachees')"
            :members-error="errors.get('coacheeIds')"
            :show-select-all-button="true"
            member-filter="coachees"
          />

          <DialogBreak />
          <FeedbackTopicList
            v-model="form.feedbackTopicId"
            :templates="filteredTemplates"
            :error="errors.get('feedbackTopicId')"
            :single-choice="true"
          />
        </template>
        <template v-else-if="step === 'select-question-groups'">
          <QuestionGroupSectionSelect
            v-model="form.questionGroupIds"
            :templates="configurableTemplates"
            :error="errors.get('questionGroupIds')"
          />
        </template>
        <template v-else-if="step === 'select-perspectives'">
          <h3
            class="text-title-small mb-2"
            :class="[
              !errors.get('perspectives') && 'text-on-surface-variant',
              errors.get('perspectives') && 'text-error',
            ]"
          >
            {{ $t('coaching.assessment.selectPerspectives') }}
          </h3>
          <div class="flex flex-col gap-4">
            <BaseSwitchListItem
              v-if="includeSelfAvailable"
              v-model="form.includeSelfFeedback"
              v-tooltip="selfRequired ? $t('startSurvey.perspectiveRequiredTooltip') : undefined"
              class="bg-surface-container-low rounded-large"
              :title="$t('coaching.includeSelfFeedback')"
              :supporting-text="$t('coaching.includeSelfFeedbackDescription')"
              icon="account_circle"
              :disabled="selfRequired"
            />

            <PerspectiveGatheringItem
              v-for="perspective in availablePerspectives"
              :key="perspective.key"
              :perspective="perspective"
              :state="ensurePerspectiveState(perspective.key)"
              :required="requiredPerspectiveKeys.has(perspective.key)"
              :error="errors.get(perspective.key)"
              :member-filter="'non-coachees'"
              :member-include-self="true"
            />
          </div>
          <p v-if="errors.get('perspectives')" class="text-error text-body-medium mt-4 px-4">
            {{ errors.get('perspectives') }}
          </p>
        </template>
        <template v-else-if="step === 'add-metadata'">
          <BaseInputField
            v-model="form.title"
            type="text"
            :label="$t('common.title')"
            class="bg-surface-container-high"
          />
          <DialogBreak />
          <h3 class="text-title-small">{{ $t('common.message') }} ({{ $t('common.optional') }})</h3>
          <WysiwygTextField v-model="form.message" :placeholder="$t('survey.messagePlaceholder')" />
        </template>
      </ul>
    </LoadingWrapper>
    <template #confirm>
      <div class="flex flex-row gap-1">
        <BaseTextButton v-if="!isFirstStep" size="M" color="text" @click="previousStep">{{
          $t('common.back')
        }}</BaseTextButton>
        <BaseTextButton v-if="!isLastStep" size="M" color="filled" @click="nextStep">{{
          $t('common.continue')
        }}</BaseTextButton>
        <BaseTextButton v-if="isLastStep" size="M" color="filled" @click="saveSurvey">{{
          $t('common.start')
        }}</BaseTextButton>
      </div>
    </template>
    <template #dismiss>
      <BaseTextButton color="text" size="M" @click="emit('close-dialog')">{{
        $t('common.cancel')
      }}</BaseTextButton>
    </template>
  </BaseDialog>
</template>
<script setup lang="ts">
  import { toRef } from 'vue'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import BaseDialog from '@/core/components/base/dialog/BaseDialog.vue'
  import BaseInputField from '@/core/components/base/form-fields/BaseInputField.vue'
  import WysiwygTextField from '@/core/components/base/wysiwyg/WysiwygTextField.vue'
  import LoadingWrapper from '@/core/components/loading/LoadingWrapper.vue'
  import FeedbackTopicList from '@/core/components/feedback-topic-list/FeedbackTopicList.vue'
  import { useStartAssessment } from '../../assessment/components/use-start-assessment'
  import DialogBreak from '@/core/components/base/dialog/DialogBreak.vue'
  import SurveyMembersForm from './SurveyMembersForm.vue'
  import BaseSwitchListItem from '@/core/components/base/switch-list-item/BaseSwitchListItem.vue'
  import QuestionGroupSectionSelect from '@/core/components/question-group-section-select/QuestionGroupSectionSelect.vue'
  import PerspectiveGatheringItem from '@/core/components/perspective-gathering/PerspectiveGatheringItem.vue'

  interface Props {
    isOpen: boolean
  }

  const props = defineProps<Props>()

  const emit = defineEmits<{
    (e: 'close-dialog'): void
    (e: 'assessment-created', surveyId: string): void
  }>()
  const {
    step,
    isFirstStep,
    isLastStep,
    nextStep,
    previousStep,
    saveSurvey,
    form,
    errors,
    filteredTemplates,
    configurableTemplates,
    availablePerspectives,
    includeSelfAvailable,
    selfRequired,
    requiredPerspectiveKeys,
    ensurePerspectiveState,
    templateLoadingStatus,
  } = useStartAssessment(toRef(props, 'isOpen'), (assessmentId) =>
    emit('assessment-created', assessmentId),
  )
</script>
